/**
 * Per-device switch for the accessibility affordances (STEM-201).
 *
 * A **preference**, not state — which is why it is module-level and persisted
 * rather than per-instance like focus, undo history or the announcer. One
 * student on one machine either wants these affordances or does not; two
 * interactives on a page should agree, and the choice should survive a reload.
 * (The per-instance things are per-instance because two graphs must *not* share
 * them — the opposite requirement.)
 *
 * Default **off**, for the reason sonification defaults off: an accommodation
 * shown to everyone by default stops reading as an accommodation and starts
 * reading as clutter. What it must never do is become undiscoverable — the
 * toggle lives first in the action bar, and the screen-reader entry point into
 * the workspace is deliberately NOT gated behind it.
 *
 * Modelled on `useSoundPreference` in graph-builder, including its handling of
 * blocked storage: private browsing throws on both read and write, and a
 * preference failing to persist is survivable where a crash on mount is not.
 */
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'alchemie:accessibility-options';

function readStored(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
}

let enabled = readStored();
const listeners = new Set<(next: boolean) => void>();

function setEnabledGlobal(next: boolean) {
  if (next === enabled) return;
  enabled = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off');
  } catch {
    // Not persisting is survivable; the toggle still works for this session.
  }
  for (const listener of listeners) listener(next);
}

export interface UseAccessibilityOptionsResult {
  readonly enabled: boolean;
  readonly setEnabled: (next: boolean) => void;
  readonly toggle: () => void;
}

export function useAccessibilityOptions(): UseAccessibilityOptionsResult {
  const [value, setValue] = useState(enabled);

  useEffect(() => {
    // Re-read on mount: another instance may have changed it before this one
    // rendered, and the module value is the truth.
    setValue(enabled);
    listeners.add(setValue);
    return () => {
      listeners.delete(setValue);
    };
  }, []);

  const setEnabled = useCallback((next: boolean) => setEnabledGlobal(next), []);
  const toggle = useCallback(() => setEnabledGlobal(!enabled), []);

  return { enabled: value, setEnabled, toggle };
}

/** Test-only: reset the module flag between cases. */
export function _resetAccessibilityOptions(): void {
  enabled = false;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  for (const listener of listeners) listener(false);
}
